import { datadogRum } from '@datadog/browser-rum'
import { Chat, HelpOutline, KeyboardArrowDown, Logout, MenuBook, NewReleasesOutlined, Settings } from '@mui/icons-material'
import { Badge, Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material'
import { signOut } from 'next-auth/react'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import { getUserFacingRoleFromUserConfig, UserFacingRole } from '@omnea-app/fe-auth/roles'
import { getTargetOrg, setTargetOrg } from '@omnea-app/fe-auth/TokenProvider'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { BEAMER_TRIGGER_ID } from '@omnea-app/fe-legacy/components/layout/wrapper/beamer'
import { useIsBeamerEnabled } from '@omnea-app/fe-legacy/components/layout/wrapper/useIsBeamerEnabled'
import { CopyIdButton } from '@omnea-app/fe-legacy/components/shared/ui/CopyIdButton/CopyIdButton'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { LoggedInUserAvatar } from '@omnea-app/fe-users-and-groups/avatar/UserAvatar'
import { useMenu } from '@omnea-app/omui/useMenu'
import { NotificationInbox } from './NotificationInbox'
import { OrganisationIndicator } from './OrganisationIndicator/OrganisationIndicator'
import { useBeamerUnreadCount } from './useBeamerUnreadCount'

type UserDetailsView = 'full' | 'compact' | 'minimal'

const KNOWLEDGE_BASE_URL = 'https://portal.usepylon.com/omnea/login?kbUrl=https://omnea-knowledge-base.help.usepylon.com/'

const PYLON_ROLE_LABELS: Record<UserFacingRole, string> = {
  [UserFacingRole.SUPER_ADMIN]: 'Super Admin',
  [UserFacingRole.ADMIN]: 'Admin',
  [UserFacingRole.VIEWER]: 'Viewer',
  [UserFacingRole.USER]: 'User',
  [UserFacingRole.SUPPLIER]: 'Supplier',
}

interface UserDetailsProps {
  view?: UserDetailsView
}

export const UserDetails = ({ view = 'full' }: UserDetailsProps) => {
  const fullView = view === 'full'
  const minimalView = view === 'minimal'

  const userData = useAuthenticatedUser()
  const helpMenu = useMenu()
  const userMenu = useMenu()
  const { t } = useTranslation('common')
  const isBeamerEnabled = useIsBeamerEnabled()
  const { RFX } = useFeatureFlags()
  const { unreadCount: beamerUnreadCount, refetchUnreadCount } = useBeamerUnreadCount({
    enabled: isBeamerEnabled,
  })

  if (!userData.isFullyAuthenticated || !userData.user) {
    return null
  }

  const { user } = userData // Reading the `user` property from the `userData` will throw an error if `isFullyAuthenticated` is false

  const hasBeamerUnread = beamerUnreadCount > 0

  const handleLogout = () => {
    const targetOrg = getTargetOrg()
    try {
      localStorage.clear()
    } catch {
      // Ignore storage errors and continue with sign out
    } finally {
      if (targetOrg) setTargetOrg(targetOrg)
      void signOut({ callbackUrl: '/login' })
    }
  }

  const showPylon = () => {
    window.Pylon('setNewIssueCustomFields', {
      user_id: user.id,
      user_email: user.email,
      user_role: PYLON_ROLE_LABELS[getUserFacingRoleFromUserConfig(user)],
      organisation_name: userData.org.name,
      organisation_id: userData.org.organisationId,
      session_url: datadogRum.getSessionReplayLink(),
    })
    window.Pylon('show')
  }

  const showBeamer = () => {
    if (window.Beamer) {
      window.Beamer.update({
        user_id: user.id,
        user_firstname: user.firstName,
        user_lastname: user.lastName,
        user_email: user.email,
        sourcing_enabled: RFX,
        onclose: refetchUnreadCount,
      })
      window.Beamer.show()
    }
  }

  const openKnowledgeBase = () => {
    window.open(KNOWLEDGE_BASE_URL, '_blank', 'noopener,noreferrer')
  }

  const showSettingsLink = !user.isSupplier

  return (
    <Stack spacing={4} direction="row" alignItems="center">
      {!minimalView && (
        <>
          <CopyIdButton id={userData.org.organisationId} entityType="organisation" />
          <NotificationInbox />
        </>
      )}
      {isBeamerEnabled && (
        <Tooltip title={t('header.product-updates-tooltip')}>
          <IconButton
            sx={{
              pr: 0,
              '&.MuiButtonBase-root:hover': {
                bgcolor: 'transparent',
              },
            }}
            onClick={showBeamer}
          >
            <Box
              component="span"
              id={BEAMER_TRIGGER_ID}
              sx={{
                position: 'absolute',
                top: 4,
                right: 6,
                width: 0,
                height: 0,
                pointerEvents: 'none',
                '& .beamer_icon': {
                  display: 'none !important',
                },
              }}
            />
            <Badge
              variant="dot"
              invisible={!hasBeamerUnread}
              sx={{
                '& .MuiBadge-dot': {
                  bgcolor: 'bg.success.default',
                  top: 4,
                  right: 4,
                  outline: '2px solid',
                  outlineColor: 'border.white',
                },
              }}
            >
              <NewReleasesOutlined sx={{ width: 23, height: 23 }} />
            </Badge>
          </IconButton>
        </Tooltip>
      )}
      <IconButton
        sx={{
          '&.MuiButtonBase-root:hover': {
            bgcolor: 'transparent',
          },
        }}
        onClick={helpMenu.open}
      >
        <HelpOutline sx={{ width: 23, height: 23, mr: fullView ? 1 : 0 }} />
        {fullView && <Typography variant="body-regular">{t('header.help')}</Typography>}
      </IconButton>
      <Menu
        open={helpMenu.isOpen}
        anchorEl={helpMenu.anchorEl}
        onClose={helpMenu.close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            helpMenu.close()
            showPylon()
          }}
        >
          <ListItemIcon>
            <Chat />
          </ListItemIcon>
          <ListItemText>{t('header.live-support')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            helpMenu.close()
            openKnowledgeBase()
          }}
        >
          <ListItemIcon>
            <MenuBook />
          </ListItemIcon>
          <ListItemText>{t('header.knowledge-base-link')}</ListItemText>
        </MenuItem>
      </Menu>
      <Stack
        direction="row"
        alignItems="center"
        alignSelf="stretch"
        sx={{
          cursor: 'pointer',
          minWidth: view === 'compact' ? 40 : 'auto',
        }}
        onClick={userMenu.open}
      >
        <Tooltip title={user.email}>
          {/* Wrapped in div so the tooltip can pass refs */}
          <div>
            <LoggedInUserAvatar user={user} />
          </div>
        </Tooltip>
        {fullView && (
          <Typography variant="body-regular" color="text.muted" sx={{ ml: 2 }}>
            {user.firstName + ' ' + user.lastName}
          </Typography>
        )}
        {fullView && <KeyboardArrowDown sx={{ color: 'fg.subtle', fontSize: 20 }} data-testid="user-menu" />}
      </Stack>
      <Menu
        open={userMenu.isOpen}
        anchorEl={userMenu.anchorEl}
        onClose={userMenu.close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 200, mt: 1 } } }}
      >
        {showSettingsLink && (
          <MenuItem component={Link} href="/settings/notifications" onClick={userMenu.close}>
            <ListItemIcon>
              <Settings />
            </ListItemIcon>
            <ListItemText>{t('header.settings')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            userMenu.close()
            handleLogout()
          }}
        >
          <ListItemIcon>
            <Logout />
          </ListItemIcon>
          <ListItemText>{t('header.logout')}</ListItemText>
        </MenuItem>
      </Menu>
      {!minimalView && <OrganisationIndicator />}
    </Stack>
  )
}
